import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { initialValues } from "../AddBooking";
import BookingLayout from "./Components/AddBookingLayout.tsx";
import BookingClientInformation from "./Components/steps/ClientInformation";
import BookingPreviewSidebar from "./Components/PreviewSidebar";
import CustomStepper from "./Components/CustomStepper";
import DeceasedInformation from "./Components/steps/DeceasedInfo";
import ConferenceServiceStep from "./Components/steps/ConferenceServiceInformation";
import FuneralServiceStep from "./Components/steps/FuneralServiceInformation";
import { BookingData, LocationState } from "../../../types/bookings";
import PackageStep from "./Components/steps/PackageSelection";
import apiService from "../../../service/Api";
import { checkPermission } from "../../../service/permissionGuard";
import { PERMISSIONS } from "../../../constants/permissions";
import { buildBookingFormData } from "../utils/add-booking-form-data";
import { BookingResponse } from "../types/add-booking";
import ConferenceStreamingStep from "./Components/steps/ConferenceStreamingStep";
import ChurchServiceStep from "./Components/steps/ChurchServiceStep";
import EventServiceStep from "./Components/steps/EventServiceStep";
import OtherServiceStep from "./Components/steps/OtherServiceStep";
import ServicesStep from "./Components/steps/ServiceStep";

export interface StepComponentProps {
  bookingData: BookingData;
  setBookingData: React.Dispatch<React.SetStateAction<BookingData>>;
  onNext: () => void;
  onBack: () => void;
  bookingType: BookingType;
  submitting?: boolean;
  isEdit?: boolean;
  onUpdate?: () => void;
}

export type BookingType =
  | "funeral"
  | "conference"
  | "church"
  | "event"
  | "funeral_home"
  | "other";

const BOOKING_STEPS: Record<
  BookingType,
  { label: string; component: React.ComponentType<StepComponentProps> }[]
> = {
  funeral: [
    {
      label: "Client",
      component:
        BookingClientInformation as React.ComponentType<StepComponentProps>,
    },
    {
      label: "Deceased",
      component: DeceasedInformation as React.ComponentType<StepComponentProps>,
    },
    {
      label: "Service",
      component: FuneralServiceStep as React.ComponentType<StepComponentProps>,
    },
    {
      label: "Package",
      component: PackageStep as React.ComponentType<StepComponentProps>,
    },
    {
      label: "Services",
      component: ServicesStep as React.ComponentType<StepComponentProps>,
    },
  ],
  funeral_home: [
    {
      label: "Client",
      component:
        BookingClientInformation as React.ComponentType<StepComponentProps>,
    },
    {
      label: "Deceased",
      component: DeceasedInformation as React.ComponentType<StepComponentProps>,
    },
    {
      label: "Service",
      component: FuneralServiceStep as React.ComponentType<StepComponentProps>,
    },
    {
      label: "Package",
      component: PackageStep as React.ComponentType<StepComponentProps>,
    },
    {
      label: "Services",
      component: ServicesStep as React.ComponentType<StepComponentProps>,
    },
  ],
  church: [
    {
      label: "Client",
      component:
        BookingClientInformation as React.ComponentType<StepComponentProps>,
    },
    {
      label: "Service",
      component: ChurchServiceStep as React.ComponentType<StepComponentProps>,
    },
    {
      label: "Package",
      component: PackageStep as React.ComponentType<StepComponentProps>,
    },
    {
      label: "Services",
      component: ServicesStep as React.ComponentType<StepComponentProps>,
    },
  ],
  event: [
    {
      label: "Client",
      component:
        BookingClientInformation as React.ComponentType<StepComponentProps>,
    },
    {
      label: "Service",
      component: EventServiceStep as React.ComponentType<StepComponentProps>,
    },
    {
      label: "Package",
      component: PackageStep as React.ComponentType<StepComponentProps>,
    },
    {
      label: "Services",
      component: ServicesStep as React.ComponentType<StepComponentProps>,
    },
  ],
  other: [
    {
      label: "Client",
      component:
        BookingClientInformation as React.ComponentType<StepComponentProps>,
    },
    {
      label: "Service",
      component: OtherServiceStep as React.ComponentType<StepComponentProps>,
    },
    {
      label: "Package",
      component: PackageStep as React.ComponentType<StepComponentProps>,
    },
    {
      label: "Services",
      component: ServicesStep as React.ComponentType<StepComponentProps>,
    },
  ],
  conference: [
    {
      label: "Client",
      component:
        BookingClientInformation as React.ComponentType<StepComponentProps>,
    },
    {
      label: "Service",
      component:
        ConferenceServiceStep as React.ComponentType<StepComponentProps>,
    },
    {
      label: "Streaming",
      component:
        ConferenceStreamingStep as React.ComponentType<StepComponentProps>,
    },
    {
      label: "Package",
      component: PackageStep as React.ComponentType<StepComponentProps>,
    },
    {
      label: "Services",
      component: ServicesStep as React.ComponentType<StepComponentProps>,
    },
  ],
};

const getBookingType = (serviceType: string): BookingType => {
  const map: Record<string, BookingType> = {
    CONFERENCE: "conference",
    FUNERAL: "funeral",
    FUNERAL_HOME: "funeral_home",
    CHURCH: "church",
    EVENT: "event",
    OTHER: "other",
  };
  return map[serviceType?.toUpperCase()] || "other";
};

const AddBookingV2 = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { serviceType, prefillData } = (location.state as LocationState) || {
    serviceType: "FUNERAL",
  };

  const bookingType = getBookingType(serviceType);
  const steps = BOOKING_STEPS[bookingType];

  const [activeStep, setActiveStep] = useState<number>(0);
  const [bookingData, setBookingData] = useState<BookingData>({
    ...initialValues,
    booking_type: serviceType,
    ...(prefillData || {}),
  });
  const [submitting, setSubmitting] = useState(false);

  const isFirstStep = activeStep === 0;
  const isLastStep = activeStep === steps.length - 1;
  const ActiveComponent = steps[activeStep]?.component;

  const submitBooking = async () => {
    setSubmitting(true);
    try {
      checkPermission(PERMISSIONS.BOOKING_ADD);
      const formData = buildBookingFormData(bookingData);
      const response = await apiService.post<BookingResponse>(
        "/api/bookings/booking/create-fullbooking/",
        formData,
      );
      toast.success("Booking has been created successfully", {
        position: "top-right",
        autoClose: 2000,
        theme: "light",
      });
      setTimeout(() => navigate(`/bookings/${response.data.id}`), 2000);
    } catch (err: unknown) {
      let errorMessage = "Failed to create booking.";
      const responseData = (
        err as {
          response?: { status?: number; data?: Record<string, unknown> };
        }
      )?.response;
      if (responseData?.status === 401) {
        navigate("/login");
        errorMessage = "Session expired. Please log in.";
      } else if ((responseData?.data as { detail?: string })?.detail) {
        errorMessage =
          (responseData?.data as { detail?: string }).detail || errorMessage;
      } else if (responseData?.data) {
        try {
          errorMessage = Object.entries(responseData.data)
            .map(([key, value]) =>
              Array.isArray(value)
                ? `${key}: ${value.join(", ")}`
                : `${key}: ${value}`,
            )
            .join("; ");
        } catch {
          errorMessage = JSON.stringify(responseData.data);
        }
      }
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = () => {
    if (isLastStep) submitBooking();
    else setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    if (activeStep > 0) setActiveStep((prev) => prev - 1);
  };

  return (
    <BookingLayout
      onNext={handleNext}
      onBack={handleBack}
      isFirstStep={isFirstStep}
      isLastStep={isLastStep}
      submitting={submitting}
      isEdit={false}
      stepperComponent={
        <CustomStepper
          steps={steps.map((s) => s.label)}
          activeStep={activeStep}
          onStepClick={setActiveStep}
        />
      }
      formContentComponent={
        ActiveComponent ? (
          <ActiveComponent
            bookingData={bookingData}
            setBookingData={setBookingData}
            onNext={handleNext}
            onBack={handleBack}
            bookingType={bookingType}
            submitting={submitting}
          />
        ) : null
      }
      sidebarContentComponent={
        <BookingPreviewSidebar
          bookingData={bookingData}
          bookingType={bookingType}
        />
      }
    />
  );
};

export default AddBookingV2;
